import React, { useMemo, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { categories, livingCategories } from '../data/collections';
import { livingNav, mainNav } from '../data/navigation';
import { site } from '../data/site';
import { collectionPath, fromPrice, productPath, productsForCollection, resolveCollection } from '../utils/products';
import { breadcrumbJsonLd, truncate } from '../utils/seo';
import { useSeo } from '../hooks/useSeo';
import { ProductTile } from '../components/shop/ProductTile';
import { NotFound } from './NotFound';
import type { CategoryId, Collection as CollectionType, Line } from '../types/product';
type Sort = 'populair' | 'prijs-op' | 'prijs-af' | 'beoordeling';
const sortLabels: Record<Sort, string> = {
  populair: 'Populairst',
  'prijs-op': 'Prijs: laag naar hoog',
  'prijs-af': 'Prijs: hoog naar laag',
  beoordeling: 'Best beoordeeld'
};
export function Collection({
  line = 'beauty'
}: {
  line?: Line;
}) {
  const {
    slug = 'alles'
  } = useParams();
  const collection = resolveCollection(slug, line);
  if (!collection) return <NotFound data-id="element-624" data-mp="1aiz0o17td12v" />;
  return <CollectionView key={`${line}-${slug}`} collection={collection} line={line} data-id="element-625" data-mp="1aifjwntwgiqf" />;
}
function CollectionView({
  collection,
  line
}: {
  collection: CollectionType;
  line: Line;
}) {
  const living = line === 'living';
  const brand = living ? site.livingName : site.name;
  const homePath = living ? '/living' : '/';
  const nav = living ? livingNav : mainNav;
  const [category, setCategory] = useState<CategoryId | 'alle'>('alle');
  const [sort, setSort] = useState<Sort>('populair');
  const all = productsForCollection(collection);
  const present = (living ? livingCategories : categories).filter(c => all.some(p => p.category === c.id));
  const showFilter = collection.kind !== 'category' && present.length > 1;
  const path = collectionPath(collection.slug, line);
  const seoText = collection.seoText ?? `Shop ${collection.title.toLowerCase()} bij ${brand}: ${all.map(p => p.name).join(', ')}. ${living ? 'Handgemaakt in Marokko, met duidelijke materiaal- en onderhoudsinformatie per product.' : 'Pure Marokkaanse verzorging met een duidelijke beschrijving, werking en gebruiksaanwijzing per product.'}`;
  useSeo({
    title: `${collection.title} – ${living ? 'Marokkaans wonen & sieraden' : 'Marokkaanse verzorging'} | ${brand}`,
    description: truncate(`${collection.intro} Shop ${all.slice(0, 3).map(p => p.name).join(', ')} en meer. Gratis verzending vanaf € 50.`),
    path,
    image: collection.image ?? all[0]?.image,
    jsonLd: [{
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: collection.title,
      description: collection.intro,
      url: `${site.url}${path}`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: all.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${site.url}${productPath(p)}`,
          name: p.name
        }))
      }
    }, breadcrumbJsonLd([{
      name: living ? site.livingName : 'Home',
      path: homePath
    }, {
      name: collection.title,
      path
    }])]
  });
  const visible = useMemo(() => {
    const list = category === 'alle' ? all : all.filter(p => p.category === category);
    const sorted = [...list];
    if (sort === 'prijs-op') sorted.sort((a, b) => fromPrice(a) - fromPrice(b));
    if (sort === 'prijs-af') sorted.sort((a, b) => fromPrice(b) - fromPrice(a));
    if (sort === 'beoordeling') sorted.sort((a, b) => b.rating - a.rating || b.reviews - a.reviews);
    return sorted;
  }, [all, category, sort]);
  const chip = (active: boolean) => living ? `whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors duration-150 ${active ? 'bg-majorelle text-white' : 'bg-surface text-ink hover:bg-sand'}` : `whitespace-nowrap border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-150 ${active ? 'border-ink bg-ink text-white' : 'border-line text-ink hover:border-ink'}`;
  return <div className="mx-auto max-w-7xl px-5 pb-24 pt-8 lg:px-8 lg:pt-10" data-id="element-626" data-mp="uyoxooftvqu7">
      <nav aria-label="Kruimelpad" className="text-sm text-muted" data-id="element-627" data-mp="qxd7agp4r1ky">
        <Link to={homePath} className="hover:text-ink" data-id="element-628" data-mp="qxli831ozwti">{living ? 'AMIRA Living' : 'Home'}</Link>
        <span className="mx-2" data-id="element-629" data-mp="m03k77p7r20a">/</span>
        <span className="text-ink" data-id="element-630" data-mp="1io9jrg7a347p">{collection.title}</span>
      </nav>

      <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-631" data-mp="1imf68myc6edn">
        <div className="lg:col-span-7" data-id="element-632" data-mp="1eo945s8g0mz2">
          <h1 className={living ? 'text-4xl font-semibold tracking-tight text-ink sm:text-5xl' : 'font-display text-4xl uppercase tracking-display text-ink sm:text-6xl'} data-id="element-633" data-mp="1el3by44ip4z4">
            {collection.title}
          </h1>
          <p className="mt-3 max-w-xl text-lg font-light text-muted" data-id="element-634" data-mp="3udspejjbgc71">{collection.intro}</p>
        </div>
        {collection.image && collection.kind === 'audience' && <img src={collection.image} alt="" className="hidden aspect-[16/9] w-full object-cover lg:col-span-5 lg:block" data-id="element-635" data-mp="3ph251k0hauft" />}
      </div>

      <div className="no-scrollbar -mx-5 mt-8 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:px-0" data-id="element-636" data-mp="3k9c9jq5u6v38">
        {nav.map(n => <NavLink key={n.to} to={n.to} className={({
        isActive
      }) => chip(isActive)} data-id="element-637" data-mp="3p7nhtwghgxjc">
            {n.label}
          </NavLink>)}
      </div>

      <div className="mt-8 flex flex-col gap-4 border-y border-line py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-638" data-mp="1wee9wjzoqfe">
        {showFilter ? <div className="no-scrollbar -mx-5 flex gap-1 overflow-x-auto px-5 sm:mx-0 sm:px-0" role="group" aria-label="Filter op categorie" data-id="element-639" data-mp="6tnzt149chxy">
            {[{
          id: 'alle' as const,
          title: 'Alles'
        }, ...present].map(c => <button key={c.id} onClick={() => setCategory(c.id)} aria-pressed={category === c.id} className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors duration-150 ${category === c.id ? 'bg-surface font-medium text-ink' : 'text-muted hover:text-ink'}`} data-id="element-640" data-mp="grc5un7bdbvf">
                {c.title}
              </button>)}
          </div> : <p className="text-sm text-muted" data-id="element-641" data-mp="2kg6v2m22fhgp">{visible.length} producten</p>}
        <div className="flex items-center justify-between gap-4 sm:justify-end" data-id="element-642" data-mp="2fhlv8nqq6zss">
          {showFilter && <p className="text-sm text-muted" data-id="element-643" data-mp="2thdeb2w3n6dn">{visible.length} producten</p>}
          <label className="relative" data-id="element-644" data-mp="2okmx2c8k64qn">
            <span className="sr-only" data-id="element-645" data-mp="2ojlelmp7qfyw">Sorteren</span>
            <select value={sort} onChange={e => setSort(e.target.value as Sort)} className={`h-10 appearance-none border border-line bg-white pl-4 pr-10 text-sm font-medium text-ink focus:border-ink focus:outline-none ${living ? 'rounded-lg' : ''}`} data-id="element-646" data-mp="3390zwn5z5t77">
              {(Object.keys(sortLabels) as Sort[]).map(s => <option key={s} value={s} data-id="element-647" data-mp="3qw4uuh6wn1ut">{sortLabels[s]}</option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" data-id="element-648" data-mp="3vuzetmglywgi" />
          </label>
        </div>
      </div>

      {visible.length === 0 ? <div className="py-24 text-center" data-id="element-649" data-mp="3mub16sv4my45">
          <p className="text-lg font-semibold" data-id="element-650" data-mp="3mszvgni4fgae">Geen producten in deze selectie</p>
          <button onClick={() => setCategory('alle')} className="mt-4 text-sm font-medium underline underline-offset-4" data-id="element-651" data-mp="384lu6h0crzvy">
            Toon alles
          </button>
        </div> : <ul className={`mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${living ? 'gap-3 sm:gap-5' : 'gap-x-4 gap-y-12 lg:gap-x-6'}`} data-id="element-652" data-mp="2z5rrc8wfid9y">
          {visible.map(p => <li key={p.slug} data-id="element-653" data-mp="2p72jl8y8y97f">
              <ProductTile product={p} data-id="element-654" data-mp="2p7u33lekkghc" />
            </li>)}
        </ul>}

      <section className="mt-24 max-w-3xl border-t border-line pt-10" data-id="element-655" data-mp="2u65cxw4v6qzk">
        <h2 className={living ? 'text-2xl font-semibold tracking-tight text-ink' : 'font-display text-3xl uppercase tracking-display text-ink'} data-id="element-656" data-mp="39nwwdza8k9o6">
          Over {collection.title.toLowerCase()}
        </h2>
        <p className="mt-4 text-[17px] font-light leading-relaxed text-muted" data-id="element-657" data-mp="30myr7do29w40">{seoText}</p>
      </section>
    </div>;
}